import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  WalletIcon,
  ArrowsClockwiseIcon,
  GiftIcon,
  TrendUpIcon,
  TicketIcon,
  CreditCardIcon,
  UserPlusIcon,
  FireIcon,
  PlusIcon,
  StarIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import AddCardDialog from "@/components/AddCardDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Wallet bottom-sheet — the in-stream "tap to see your balance & credits"
 * surface that sits behind the right-rail Wallet icon.  Mirrors the way
 * live sale platforms tucks the wallet inside the live show so buyers never have to
 * leave the broadcast to check what they have to spend.
 *
 * Pulls from `GET /api/me/credits` which returns
 *   { balance_cents, entries: [{ id, amount_cents, reason, meta, created_at }] }
 *
 * Unauthenticated users see a sign-in CTA instead of a balance.
 */

const REASON_META = {
  referral_signup:        { label: "Referral signup bonus",  icon: UserPlusIcon },
  referral_bidder_first_bid: { label: "Friend's first bid", icon: UserPlusIcon },
  streak_7_day:           { label: "7-day login streak",     icon: FireIcon },
  streak_30_day:          { label: "30-day login streak",    icon: FireIcon },
  plus_cashback:          { label: "Plus cashback",          icon: TrendUpIcon },
  checkout_redeem:        { label: "Redeemed at checkout",   icon: CreditCardIcon },
  admin_grant:            { label: "Bidorah credit",         icon: GiftIcon },
};

function fmtUSD(cents) {
  const n = Math.max(0, cents || 0) / 100;
  return `$${n.toFixed(2)}`;
}

function fmtSignedUSD(cents) {
  const n = (cents || 0) / 100;
  const sign = n >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      ...(sameYear ? {} : { year: "numeric" }),
    });
  } catch {
    return "";
  }
}

export default function WalletSheet({ open, onOpenChange, currentUser }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Saved-cards state.  Fetched from /me/wallet/payment-methods only when
  // we have a logged-in user (anon viewers see the sign-in CTA instead).
  const [cards, setCards] = useState([]);
  const [defaultPm, setDefaultPm] = useState(null);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [busyPm, setBusyPm] = useState("");
  const [addCardOpen, setAddCardOpen] = useState(false);
  // Card-remove confirmation. Drives the styled AlertDialog so we don't
  // fall back to the OS window.confirm() prompt (out-of-theme on mobile).
  const [pendingRemovePm, setPendingRemovePm] = useState(null);

  const refresh = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const { data } = await api.get("/me/credits");
      setData(data);
    } catch {
      // soft fail — wallet just shows the cached user balance
    } finally {
      setLoading(false);
    }
  };

  const refreshCards = async () => {
    if (!currentUser) return;
    setCardsLoading(true);
    try {
      const { data } = await api.get("/me/wallet/payment-methods");
      setCards(data.cards || []);
      setDefaultPm(data.default_pm || null);
    } catch (e) {
      // Soft-fail — a 502/network error shouldn't blow up the sheet.
      // Real toast for permission/auth issues only.
      const msg = e?.response?.status >= 500 ? null : apiError(e);
      if (msg) toast.error(msg);
    } finally {
      setCardsLoading(false);
    }
  };

  const handleSetDefault = async (pmId) => {
    setBusyPm(pmId);
    try {
      await api.post(`/me/wallet/payment-methods/${pmId}/default`);
      setDefaultPm(pmId);
      toast.success("Default card updated");
      refreshCards();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyPm("");
    }
  };

  const handleRemove = async (pmId) => {
    if (busyPm) return;
    if (!window.confirm("Remove this card from your wallet?")) return;
    setBusyPm(pmId);
    try {
      await api.delete(`/me/wallet/payment-methods/${pmId}`);
      toast.success("Card removed");
      refreshCards();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyPm("");
    }
  };

  // Reload whenever the sheet opens. Snappy because /me/credits is just
  // a single doc read + a 25-row find.
  useEffect(() => {
    if (open) {
      refresh();
      refreshCards();
    }
  }, [open]);

  // Optimistic seed from the auth-context user record so the balance
  // shows instantly while the network request is in flight.
  const seedCents = Number(currentUser?.credit_balance_cents || 0);
  const balanceCents = data?.balance_cents ?? seedCents;
  const entries = data?.entries || [];

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl p-0 max-h-[82vh] overflow-hidden flex flex-col bg-zinc-950 border-t border-white/10 text-white"
        data-testid="wallet-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-2 text-left">
          <SheetTitle className="text-white text-base flex items-center gap-2">
            <WalletIcon size={20} weight="fill" className="text-amber-400" />
            Wallet
          </SheetTitle>
        </SheetHeader>

        {/* Balance hero — big number, Plus & Recruit shortcuts. */}
        <div className="px-5 pt-2 pb-4">
          {!currentUser ? (
            <div className="rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 p-5 text-zinc-900">
              <div className="text-xs font-bold uppercase tracking-wider opacity-80">
                Bidorah credit
              </div>
              <div className="font-display font-black text-3xl mt-1">
                Sign in to view balance
              </div>
              <button
                type="button"
                onClick={() => { onOpenChange(false); navigate("/login"); }}
                className="mt-3 h-10 px-5 rounded-full bg-zinc-900 text-white text-sm font-black"
                data-testid="wallet-signin-btn"
              >
                Sign in
              </button>
            </div>
          ) : (
            <div
              className="rounded-2xl p-5 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #0a3a5c 0%, #1D78AB 45%, #4FC3F7 100%)",
              }}
              data-testid="wallet-balance-card"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-sky-100/80">
                    Bidorah credit
                  </div>
                  <div
                    className="font-display font-black text-4xl mt-1 leading-none"
                    data-testid="wallet-balance-amount"
                  >
                    {fmtUSD(balanceCents)}
                  </div>
                  <div className="text-[11px] text-sky-100/80 mt-2">
                    Auto-applied at checkout. Stack with Plus for 1% cashback.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={refresh}
                  disabled={loading}
                  className="h-8 w-8 grid place-items-center rounded-full bg-white/10 hover:bg-white/15 transition disabled:opacity-50"
                  title="Refresh balance"
                  data-testid="wallet-refresh-btn"
                >
                  <ArrowsClockwiseIcon
                    size={14}
                    weight="bold"
                    className={loading ? "animate-spin" : ""}
                  />
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => { onOpenChange(false); navigate("/recruit"); }}
                  className="h-10 rounded-full bg-white/15 hover:bg-white/20 text-white text-xs font-bold flex items-center justify-center gap-1.5"
                  data-testid="wallet-earn-btn"
                >
                  <UserPlusIcon size={14} weight="bold" /> Earn more
                </button>
                <button
                  type="button"
                  onClick={() => { onOpenChange(false); navigate("/plus"); }}
                  className="h-10 rounded-full bg-amber-400 hover:bg-amber-300 text-zinc-900 text-xs font-black flex items-center justify-center gap-1.5"
                  data-testid="wallet-plus-btn"
                >
                  <TrendUpIcon size={14} weight="bold" /> Get cashback
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Payment methods — saved cards via Stripe SetupIntent.  Cards are
            captured client-side by Stripe Elements; we only ever see the
            pm_xxx ID.  Shown only for signed-in users. */}
        {currentUser && (
          <div className="px-5 pb-1">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold">
                Payment methods
              </div>
              <button
                type="button"
                onClick={() => setAddCardOpen(true)}
                className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-white/10 hover:bg-white/15 text-[11px] font-bold text-white"
                data-testid="wallet-add-card-btn"
              >
                <PlusIcon size={12} weight="bold" /> Add card
              </button>
            </div>
            {cardsLoading && cards.length === 0 ? (
              <div className="rounded-2xl border border-white/10 px-4 py-4 text-sm text-white/55 text-center">
                Loading cards…
              </div>
            ) : cards.length === 0 ? (
              <button
                type="button"
                onClick={() => setAddCardOpen(true)}
                className="w-full rounded-2xl border border-dashed border-white/15 px-4 py-4 text-left hover:border-amber-300/50 transition flex items-center gap-3"
                data-testid="wallet-add-first-card"
              >
                <span className="h-9 w-9 grid place-items-center rounded-full bg-amber-400/15 text-amber-300">
                  <CreditCardIcon size={18} weight="fill" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">Add a credit card</div>
                  <div className="text-[11px] text-white/55">
                    One-tap bidding · Stripe-encrypted
                  </div>
                </div>
                <PlusIcon size={16} weight="bold" className="text-white/55" />
              </button>
            ) : (
              <ul className="space-y-2" data-testid="wallet-cards-list">
                {cards.map((c) => (
                  <li
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
                      c.is_default
                        ? "border-amber-300/60 bg-amber-400/5"
                        : "border-white/10 bg-white/[0.02]"
                    }`}
                    data-testid={`wallet-card-${c.id}`}
                  >
                    <span className="h-9 w-12 grid place-items-center rounded-md bg-white/10 text-[10px] font-black uppercase tracking-wider text-white">
                      {c.brand}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        •••• {c.last4}
                        {c.is_default && (
                          <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-amber-300">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-white/55">
                        Exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}
                      </div>
                    </div>
                    {!c.is_default && (
                      <button
                        type="button"
                        onClick={() => handleSetDefault(c.id)}
                        disabled={busyPm === c.id}
                        className="h-8 w-8 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-50"
                        title="Set as default"
                        data-testid={`wallet-card-default-${c.id}`}
                      >
                        <StarIcon size={14} weight="bold" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setPendingRemovePm(c.id)}
                      disabled={busyPm === c.id}
                      className="h-8 w-8 grid place-items-center rounded-full bg-white/5 hover:bg-rose-500/20 hover:text-rose-300 disabled:opacity-50"
                      title="Remove card"
                      data-testid={`wallet-card-remove-${c.id}`}
                    >
                      <TrashIcon size={14} weight="bold" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Activity feed — recent credit ledger entries. */}
        <div className="px-5 pt-4 pb-3 flex items-baseline justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-bold">
            Recent activity
          </div>
          {currentUser && entries.length > 0 && (
            <button
              type="button"
              onClick={() => { onOpenChange(false); navigate("/dashboard"); }}
              className="text-[11px] font-bold text-sky-300 hover:text-sky-200"
              data-testid="wallet-history-btn"
            >
              See all →
            </button>
          )}
        </div>
        <div
          className="flex-1 overflow-y-auto px-2 pb-[max(env(safe-area-inset-bottom,0px),96px)]"
          data-testid="wallet-activity-list"
        >
          {!currentUser ? null : loading && entries.length === 0 ? (
            <div className="px-3 py-10 text-center text-sm text-white/50">
              Loading activity…
            </div>
          ) : entries.length === 0 ? (
            <div className="mx-3 rounded-2xl border border-dashed border-white/15 p-6 text-center">
              <TicketIcon size={26} weight="duotone" className="mx-auto text-amber-300 mb-2" />
              <div className="text-sm font-bold">No credit activity yet</div>
              <div className="text-xs text-white/55 mt-1">
                Refer a friend or hit a 7-day login streak to start earning.
              </div>
            </div>
          ) : (
            <ul className="space-y-1">
              {entries.map((e) => {
                const meta = REASON_META[e.reason] || { label: e.reason, icon: GiftIcon };
                const Icon = meta.icon;
                const isCredit = (e.amount_cents || 0) > 0;
                return (
                  <li
                    key={e.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.04]"
                    data-testid={`wallet-entry-${e.id}`}
                  >
                    <span
                      className={`h-9 w-9 rounded-full grid place-items-center flex-shrink-0 ${
                        isCredit ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"
                      }`}
                    >
                      <Icon size={16} weight="fill" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {meta.label}
                      </div>
                      <div className="text-[11px] text-white/50">
                        {fmtWhen(e.created_at)}
                      </div>
                    </div>
                    <div
                      className={`font-mono font-black text-sm ${
                        isCredit ? "text-emerald-300" : "text-rose-300"
                      }`}
                    >
                      {fmtSignedUSD(e.amount_cents)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
    {/* Add-card modal — Stripe Elements lives behind this. Mounted OUTSIDE
        the Sheet so its `position: fixed` is scoped to the viewport, not
        the transformed SheetContent. */}
    <AddCardDialog
      open={addCardOpen}
      onClose={() => setAddCardOpen(false)}
      onSaved={() => {
        setAddCardOpen(false);
        refreshCards();
      }}
      currentUser={currentUser}
    />
    {/* Remove-card confirmation — styled AlertDialog (no window.confirm). */}
    <AlertDialog
      open={!!pendingRemovePm}
      onOpenChange={(o) => { if (!o) setPendingRemovePm(null); }}
    >
      <AlertDialogContent data-testid="wallet-remove-card-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove this card?</AlertDialogTitle>
          <AlertDialogDescription>
            You can add it back any time. Stripe will detach it from your
            account immediately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="wallet-remove-card-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-rose-600 hover:bg-rose-500 text-white"
            onClick={() => pendingRemovePm && handleRemove(pendingRemovePm)}
            data-testid="wallet-remove-card-confirm"
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
